import { Tween } from '../tweenjs/Tween.js';
import { quat, vec3 } from 'gl-matrix';
import Interpolation from '../tweenjs/Interpolation.js';
import { extractTransformData, findByPath, } from './utils/utils.js';
import { WrapMode } from './WrapMode.js';
const tempVecPosition = vec3.create();
const tempVecScale = vec3.create();
const tempQuatRotation = quat.create();
class AnimationTransformCache {
    static _cache = new Map();
    static add(data) {
        this._cache.set(data.guid, data);
    }
    static get(guid) {
        return this._cache.get(guid);
    }
}
/**
 * A Tween that can be attached to a Object3D.
 * It only tweens the position, rotation and scale of the object.
 * And only if the values are in the animation.
 * It creates 1 tween fot the entire animation.
 *
 * Possible (micro) optimizations:
 * - If a value is not changed throughout the animation, leave it out of the tween and
 *   only set it at the start.
 * - Do not use .ts files with the animation, but have ESBuild import the JSON directly.
 * - Somehow figure out a way to import the animations dynamically. (Async import?)
 */
export class Object3DTween extends Tween {
    object;
    _paths;
    _objects = new Map();
    _originalTransforms = new Map();
    attach(obj) {
        this.object = obj;
        this.onUpdate(this._updateObject);
        this.onEveryStart(this._updateObject);
        this.onStop(this._resetObject);
        this.onComplete(this._resetObject);
        // recusively get the position, rotation and scale of the object and store
        // to reset later.
        this._storeOriginalTransforms(obj);
        return this;
    }
    _storeOriginalTransforms(object) {
        const id = object._id;
        const transform = object.getTransformLocal();
        this._originalTransforms.set(id, transform);
        for (let i = 0; i < object.children.length; i++) {
            this._storeOriginalTransforms(object.children[i]);
        }
    }
    _resetOriginalTransforms(object) {
        const id = object._id;
        const transform = this._originalTransforms.get(id);
        if (transform) {
            object.setTransformLocal(transform);
            for (let i = 0; i < object.children.length; i++) {
                this._storeOriginalTransforms(object.children[i]);
            }
        }
    }
    static FromTransformData(obj, r, data) {
        const tween = new Object3DTween(r.from)
            .to(r.to, data.duration * 1000)
            .interpolation(Interpolation.CatmullRom)
            .attach(obj);
        switch (data.wrapMode) {
            case WrapMode.Loop:
                tween.repeat(Infinity);
                break;
        }
        for (let i = 0; i < r.paths.length; i++) {
            const objectNames = r.paths[i].split('/');
            const o = findByPath(obj, objectNames);
            if (!o) {
                console.error('Object not found:', objectNames);
                continue;
            }
            tween._objects.set(r.paths[i], o);
        }
        tween._paths = r.paths;
        return tween;
    }
    static FromData(obj, data) {
        let r = AnimationTransformCache.get(data.guid);
        if (!r) {
            r = extractTransformData(data);
            AnimationTransformCache.add(r);
        }
        // We need to clone the object, because otherwise the cache is modified
        r = structuredClone(r);
        return Object3DTween.FromTransformData(obj, r, data);
    }
    _updateObject = (v) => {
        for (let i = 0; i < this._paths.length; i++) {
            const obj = this._objects.get(this._paths[i]);
            if (!obj) {
                console.warn('Object not found:', this._paths[i]);
                continue;
            }
            const value = v[this._paths[i]];
            if (!value) {
                console.warn('Value not found:', this._paths[i]);
                continue;
            }
            let pos = value.localposition;
            let rot = value.localrotation;
            let scale = value.localscale;
            if (pos) {
                // Note, the x-axis seems to be inverted when coming from Unity.
                obj.setPositionLocal([-pos.x, pos.y, pos.z]);
            }
            if (rot) {
                obj.setRotationLocal([rot.x, rot.y, rot.z, rot.w]);
            }
            if (scale) {
                obj.setScalingLocal([scale.x, scale.y, scale.z]);
            }
        }
        // this.pos[0] = v.pos.x;
        // this.pos[1] = v.pos.y;
        // this.pos[2] = v.pos.z;
        // this.object.setPositionLocal(this.pos);
        // this.rot[0] = v.rot.x;
        // this.rot[1] = v.rot.y;
        // this.rot[2] = v.rot.z;
        // this.rot[3] = v.rot.w;
        // this.object.setRotationLocal(this.rot);
    };
    _resetObject = () => {
        //TK: Maybe this should not happen for all WrapModes, like ClampForever?
        this._resetOriginalTransforms(this.object);
    };
}
//# sourceMappingURL=Object3DTween.js.map